<template>
  <div id="staffRoleContainer">
    <!-- Back Button -->
    <CRow class="mb-2" id="staffRoleBackRow">
      <CCol md="12" id="staffRoleBackCol">
        <button
          class="btn btn-light btn-sm"
          @click="$emit('back')"
          id="staffRoleBackButton"
        >
          <i class="fi fi-rr-arrow-left mr-1"></i> {{ $t('back') }}
        </button>
      </CCol>
    </CRow>

    <CRow id="staffRoleHeaderRow">
      <CCol md="12" class="text-center" id="staffRoleHeaderCol">
        <div class="d-inline-flex align-items-start" id="staffRoleHeaderTitle">
          <h2 class="font-weight-medium text-dark mb-0" id="staffRoleTitle">
            {{ $t('staffRoleManagement') }}
          </h2>
          <HelpButton :isHeader="false" class="ml-2 mt-2" id="staffRoleHelpButton"></HelpButton>
        </div>
        <h6 class="text-description " id="staffRoleDescription">
          {{ $t('staffRoleDescription') }}
        </h6>
      </CCol>
    </CRow>
    <hr />

    <!-- Add Role Button -->
    <CRow class="mb-3" id="staffRoleActionRow">
      <CCol md="12" class="text-right" id="staffRoleActionCol">
        <button
          class="btn btn-success btn-sm"
          @click="openCreateModal()"
          id="staffRoleAddButton"
          style="border-radius: 8px; padding: 6px 16px; font-weight: 500;"
        >
          <i class="fi fi-rr-plus mr-1"></i> {{ $t('addRole') }}
        </button>
      </CCol>
    </CRow>

    <!-- Summary -->
    <!-- <div class="d-flex mb-3" style="gap: 12px;">
      <div class="sr-stat-card">
        <div class="sr-stat-num">{{ roles.length }}</div>
        <div class="sr-stat-label">{{ $t('totalRoles') || 'บทบาททั้งหมด' }}</div>
      </div>
    </div> -->

    <!-- Role List -->
    <div v-if="loading" class="text-center p-4 mb-3" id="staffRoleLoading">
      <CSpinner color="success" />
    </div>

    <ul v-else class="list-group list-group-flush sr-list mb-3" id="staffRoleList">
      <li
        class="list-group-item sr-list-item"
        v-for="(role, index) in roles"
        :key="role.objectId"
        :id="`staffRoleItem-${index}`"
      >
        <div class="d-flex align-items-center">
          <div class="sr-avatar" :style="{ backgroundColor: getRoleColor(index) }">
            <i class="fi fi-rr-shield-check"></i>
          </div>
          <div class="flex-grow-1 ml-3">
            <span class="font-weight-medium text-dark" style="font-size: 15px;">{{ role.name }}</span>
            <div class="mt-1">
              <span class="sr-perm-badge">
                <i class="fi fi-rr-check-circle" style="font-size: 10px;"></i>
                {{ countEnabledPermissions(role) }} {{ $t('permissionsEnabled') }}
              </span>
            </div>
          </div>
          <div class="d-flex align-items-center" style="gap: 6px;">
            <button
              @click="openEditModal(role)"
              class="btn btn-outline-dark btn-sm sr-edit-btn"
              :id="`staffRoleEditButton-${index}`"
            >
              <i class="fi fi-rr-edit mr-1" style="font-size: 11px;"></i> {{ $t('edit') }}
            </button>
            <button
              @click="confirmDelete(role)"
              class="btn btn-outline-danger btn-sm"
              style="border-radius: 8px; padding: 5px 10px; font-size: 13px;"
              :id="`staffRoleDeleteButton-${index}`"
            >
              <i class="fi fi-rr-trash" style="font-size: 11px;"></i>
            </button>
          </div>
        </div>
      </li>
      <li v-if="roles.length === 0" class="list-group-item text-center text-muted py-5" id="staffRoleEmptyItem">
        <i class="fi fi-rr-shield-check" style="font-size: 32px; color: #ddd;"></i>
        <div class="mt-2">
        {{ $t('noRolesFound') }}
        </div>
      </li>
    </ul>
    <pagination :meta_data="meta_data" v-on:next="getStaffRoles" ></pagination>

    <!-- Create / Edit Modal -->
    <modal
      id="staffRoleModal"
      :show.sync="showModal"
      :loading="!loadingButton"
      :title="isEditing ? $t('editRole') : $t('createRole')"
      centered
      size="xl"
      color="success"
      @isSave="saveRole()"
    >

      <CAlert color="success" v-if="successAlert !== ''" id="staffRoleSuccessAlert">
        {{ successAlert }}
      </CAlert>
      <CAlert color="danger" v-if="errorAlert !== ''" id="staffRoleErrorAlert">
        {{ errorAlert }}
      </CAlert>

      <form class="mt-3" id="staffRoleForm">
        <!-- Role Name -->
        <div class="sr-form-group" id="staffRoleNameRow">
          <label class="sr-form-label">
            <i class="fi fi-rr-shield-check mr-1"></i> {{ $t('roleName') }}
          </label>
            <input
              type="text"
              v-model="formData.name"
              class="form-control sr-form-input"
              :placeholder="$t('roleNamePlaceholder')"
              id="staffRoleNameInput"
            />
        </div>

        <hr />

        <!-- Permission Section -->
        <h5 class="font-weight-medium mb-3" id="staffRolePermissionTitle" style="font-size: 16px;">
          <i class="fi fi-rr-lock mr-1"></i> {{ $t('permissions') }}
        </h5>

        <div style="max-height: calc(100vh - 350px); overflow-y: auto;">
        <div
          v-for="(group, groupIndex) in permissionGroups"
          :key="groupIndex"
          class="sr-perm-group mb-4"
          :id="`staffRolePermGroup-${groupIndex}`"
        >
          <h6 class="sr-perm-group-title" :id="`staffRolePermGroupTitle-${groupIndex}`">
            {{ group.title }}
          </h6>
          <div
            v-for="(perm, permIndex) in group.permissions"
            :key="perm.key"
            class="sr-perm-item"
            :id="`staffRolePerm-${groupIndex}-${permIndex}`"
          >
            <div>
              <span class="sr-perm-label">{{ perm.label }}</span>
              <div class="sr-perm-desc">{{ perm.description }}</div>
            </div>
            <CSwitch
              v-model="formData[perm.key]"
              color="success"
              :checked.sync="formData[perm.key]"
              :id="`staffRolePermSwitch-${groupIndex}-${permIndex}`"
            />
          </div>
        </div>
        </div>
      </form>

    </modal>

    <!-- Confirm Delete Role Modal -->
    <modal
      id="staffRoleDeleteModal"
      :show.sync="confirmDeleteModal"
      :loading="!deleteLoading"
      :title="$t('confirmDeleteRole') || 'ยืนยันการลบบทบาท'"
      centered
      color="danger"
      @isSave="deleteRole()"
    >
      <p class="text-dark mb-3" style="font-size: 14px;">{{ $t('confirmDeleteRoleDesc') }} <b>"{{ deleteRoleData.name }}"</b></p>
      <div class="p-3 mb-2" style="background: #fff8e1; border-radius: 10px; border: 1px solid #ffe082;">
        <div class="d-flex align-items-start mb-2">
          <i class="fi fi-rr-triangle-warning mr-2" style="color: #f9a825; font-size: 14px; margin-top: 2px; flex-shrink: 0;"></i>
          <span style="font-weight: 600; color: #6d5b00; font-size: 13px;">{{ $t('pleaseNote') || 'โปรดทราบ' }}</span>
        </div>
        <ul style="margin: 0; padding-left: 20px; color: #6d5b00; font-size: 12px; line-height: 1.8;">
          <li>{{ $t('deleteRoleWarning1') || 'พนักงานที่ใช้บทบาทนี้อยู่จะยังคงสิทธิ์การใช้งานเดิมไว้ แต่จะไม่มีบทบาทกำกับอีกต่อไป' }}</li>
          <li>{{ $t('deleteRoleWarning2') || 'บทบาทนี้จะถูกลบออกจากทุกสาขาที่ใช้งานร่วมกัน' }}</li>
          <li style="color: #c62828; font-weight: 500;">{{ $t('deleteRoleWarning3') || 'การลบไม่สามารถกู้คืนได้' }}</li>
        </ul>
      </div>
    </modal>
  </div>
</template>

<script>
import service from '@/services/local'
import { mapGetters } from 'vuex'
import permis from '@/util/permission'
import util from '@/util/util'
import HelpButton from '../../containers/HelpButton.vue'
import Modal from '../../containers/Modal.vue'
import Pagination from "@/containers/Pagination";

export default {
  components: {
    HelpButton,
    Modal,
    Pagination
  },
  data() {
    return {
      loadingButton: false,
      loading: false,
      showModal: false,
      footer: '',
      roles: [],
      isEditing: false,
      successAlert: '',
      errorAlert: '',
      formData: this.getDefaultFormData(),
      confirmDeleteModal: false,
      deleteLoading: false,
      deleteRoleData: {},
      meta_data: {
        last_page: null,
        current_page: 1,
        prev_page_url: null,
        items: 0,
        limit: 20,
        itemCount: null,
      },
    }
  },
  created() {
    if (this.shopObjectId) {
      this.getStaffRoles()
    }
  },
  watch: {
    shopObjectId(val) {
      if (val) {
        this.getStaffRoles()
      }
    },
    confirmDeleteModal(val) {
      if (val) {
        this.$nextTick(() => {
          const overlay = document.createElement('div')
          overlay.id = 'sr-delete-overlay'
          overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:1055;'
          document.body.appendChild(overlay)
        })
      } else {
        const overlay = document.getElementById('sr-delete-overlay')
        if (overlay) overlay.remove()
      }
    },
  },
  computed: {
    ...mapGetters(['shops', 'users', 'date', 'isOwner']),
    permissionGroups() {
      return [
        {
          title: this.$t('generalPermission'),
          permissions: [
            { key: 'productManagementAccess', label: this.$t('permProductManagement'), description: this.$t('permProductManagementDesc') },
            { key: 'billManagementAccess', label: this.$t('permBillManagement'), description: this.$t('permBillManagementDesc') },
            { key: 'cancelBillAccess', label: this.$t('permCancelBill'), description: this.$t('permCancelBillDesc') },
            { key: 'cancelOrderAccess', label: this.$t('permCancelOrder'), description: this.$t('permCancelOrderDesc') },
            { key: 'inventoryAccess', label: this.$t('permInventory'), description: this.$t('permInventoryDesc') },
            { key: 'reportAccess', label: this.$t('permReport'), description: this.$t('permReportDesc') },
            { key: 'settingAccess', label: this.$t('permSetting'), description: this.$t('permSettingDesc') },
            { key: 'shopSettingsAccess', label: this.$t('permShopSettings'), description: this.$t('permShopSettingsDesc') },
            { key: 'customerAccess', label: this.$t('permCustomer'), description: this.$t('permCustomerDesc') },
            { key: 'drawerOpenAccess', label: this.$t('permDrawerOpen'), description: this.$t('permDrawerOpenDesc') },
            { key: 'userCashierAccess', label: this.$t('permUserCashier'), description: this.$t('permUserCashierDesc') },
            { key: 'netProfitAccess', label: this.$t('permNetProfit'), description: this.$t('permNetProfitDesc') },
            { key: 'drawerCashDetailAccess', label: this.$t('permDrawerCashDetail'), description: this.$t('permDrawerCashDetailDesc') },
          ],
        },
        {
          title: this.$t('inventoryPermission'),
          permissions: [
            { key: 'stockInCreateAccess', label: this.$t('permStockInCreate'), description: this.$t('permStockInCreateDesc') },
            { key: 'stockInEditAccess', label: this.$t('permStockInEdit'), description: this.$t('permStockInEditDesc') },
            { key: 'stockOutCreateAccess', label: this.$t('permStockOutCreate'), description: this.$t('permStockOutCreateDesc') },
            { key: 'stockOutEditAccess', label: this.$t('permStockOutEdit'), description: this.$t('permStockOutEditDesc') },
            { key: 'adjustStockAccess', label: this.$t('permAdjustStock'), description: this.$t('permAdjustStockDesc') },
          ],
        },
      ]
    },
    isPermission() {
      return permis.findPermissionRead('setting', this.$route.path)
    },
    shopObjectId: {
      get() {
        return this.$store.getters.shopObjectId
      },
      set(newValue) {
        return this.$store.dispatch('setShop', newValue)
      },
    },
  },
  methods: {
    getRoleColor(index) {
      const colors = ['#41b883', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#ef4444', '#84cc16'];
      return colors[index % colors.length];
    },
    getDefaultFormData() {
      return {
        name: '',
        enabled: true,
        // General Permission
        productManagementAccess: true,
        billManagementAccess: true,
        cancelBillAccess: true,
        cancelOrderAccess: false,
        inventoryAccess: true,
        reportAccess: true,
        settingAccess: true,
        shopSettingsAccess: false,
        customerAccess: true,
        drawerOpenAccess: true,
        userCashierAccess: true,
        netProfitAccess: true,
        drawerCashDetailAccess: false,
        // Inventory Permission
        stockInCreateAccess: true,
        stockInEditAccess: true,
        stockOutCreateAccess: true,
        stockOutEditAccess: true,
        adjustStockAccess: true,
      }
    },
    getStaffRoles(page = 1) {
      this.loading = true
      const headers = {
        shopObjectId: this.shopObjectId,
      }
       const params = {
        shopObjectId: this.shopObjectId,
        page: page,
        limit: 50,
      }

      service
        .get('/api/v3.0/staffrole', { headers, params })
        .then((res) => {
          if (res.data.error && res.data.error.code === 0) {
            this.roles = res.data.data || []
          } else {
            this.roles = res.data.data || res.data || []
          }

          if (res.data.paginate.items == undefined) {
            this.meta_data.items = 0
          } else {
            this.meta_data.items = res.data.paginate.items
          }
          this.meta_data.last_page = res.data.paginate.pageCount
          this.meta_data.current_page = res.data.paginate.currentPage
          this.meta_data.itemCount = res.data.paginate.itemCount
          this.meta_data.limit = res.data.paginate.perPage
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },
    openCreateModal() {
      this.isEditing = false
      this.formData = this.getDefaultFormData()
      this.successAlert = ''
      this.errorAlert = ''
      this.showModal = true
    },
    confirmDelete(role) {
      this.deleteRoleData = { objectId: role.objectId, name: role.name }
      this.confirmDeleteModal = true
    },
    openEditModal(role) {
      this.isEditing = true
      this.formData = { ...role }
      this.deleteRoleData = { objectId: role.objectId, name: role.name }
      this.successAlert = ''
      this.errorAlert = ''
      this.showModal = true
    },
    saveRole() {
      if (!this.formData.name || this.formData.name.trim() === '') {
        this.errorAlert = this.$t('pleaseEnterRoleName')
        return
      }

      this.loadingButton = true
      this.errorAlert = ''
      this.successAlert = ''

      const headers = {
        shopobjectid: this.shopObjectId,
        'content-type': 'application/json',
      }

      const payload = { ...this.formData }

      if (this.isEditing) {
        // PATCH — update existing role
        service
          .patch('/api/v3.0/staffrole', payload, { headers })
          .then((res) => {
            if (res.data.error && res.data.error.code === 0) {
              this.successAlert = this.$t('saveSuccess')
              this.getStaffRoles()
              this.$emit('rolesUpdated')
              this.showModal = false
            } else {
              this.errorAlert = (res.data.error && res.data.error.message) || this.$t('errorOccurred')
            }
            this.loadingButton = false
          })
          .catch(() => {
            this.errorAlert = this.$t('connectionError')
            this.loadingButton = false
          })
      } else {
        // POST — create new role
        service
          .post('/api/v3.0/staffrole', payload, { headers })
          .then((res) => {
            if (res.data.error && res.data.error.code === 0) {
              this.successAlert = this.$t('roleCreatedSuccess')
              this.getStaffRoles()
              this.$emit('rolesUpdated')
              this.showModal = false
            } else {
              this.errorAlert = (res.data.error && res.data.error.message) || this.$t('errorOccurred')
            }
            this.loadingButton = false
          })
          .catch(() => {
            this.errorAlert = this.$t('connectionError')
            this.loadingButton = false
          })
      }
    },
    deleteRole() {
      this.deleteLoading = true
      const headers = {
        shopobjectid: this.shopObjectId,
      }
      const data = {
        objectId: this.deleteRoleData.objectId,
      }
      service
        .delete('/api/v3.0/staffrole', { headers, data })
        .then((res) => {
          if (res.data.success !== false) {
            this.confirmDeleteModal = false
            this.showModal = false
            this.getStaffRoles()
            this.$emit('rolesUpdated')
          } else {
            this.errorAlert = (res.data.error?.message) || this.$t('errorOccurred')
          }
          this.deleteLoading = false
        })
        .catch((err) => {
          console.error('Delete role error:', err)
          this.deleteLoading = false
          this.errorAlert = this.$t('connectionError')
        })
    },
    countEnabledPermissions(role) {
      const permKeys = [
        'productManagementAccess', 'billManagementAccess', 'cancelBillAccess',
        'cancelOrderAccess', 'inventoryAccess', 'reportAccess', 'settingAccess',
        'customerAccess', 'drawerOpenAccess', 'userCashierAccess', 'netProfitAccess',
        'drawerCashDetailAccess', 'shopSettingsAccess',
        'stockInCreateAccess', 'stockInEditAccess', 'stockOutCreateAccess',
        'stockOutEditAccess', 'adjustStockAccess',
      ]
      return permKeys.filter((k) => role[k] === true).length
    },
  },
}
</script>

<style scoped>
/* Summary Stats */
.sr-stat-card {
  flex: 1;
  max-width: 200px;
  background: #f0faf5;
  border-radius: 10px;
  padding: 12px 16px;
  text-align: center;
  border: 1px solid #c8e6c9;
}
.sr-stat-num { font-size: 22px; font-weight: 700; color: #2e7d32; }
.sr-stat-label { font-size: 12px; color: #888; margin-top: 2px; }

/* Role List */
.sr-list { border-radius: 10px; overflow: hidden; border: 1px solid #e9ecef; }
.sr-list-item {
  padding: 14px 16px;
  transition: background-color 0.15s;
  border-bottom: 1px solid #f0f0f0;
}
.sr-list-item:last-child { border-bottom: none; }
.sr-list-item:hover { background-color: #fafffe; }

/* Avatar */
.sr-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
}

/* Permission Badge */
.sr-perm-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
  background: #e8f5e9;
  color: #2e7d32;
  font-weight: 500;
}

/* Edit Button */
.sr-edit-btn {
  border-radius: 8px;
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.15s;
}
.sr-edit-btn:hover { background: #333; color: white; }

/* Modal Form */
.sr-form-group { margin-bottom: 16px; }
.sr-form-label { font-weight: 500; font-size: 14px; color: #444; margin-bottom: 6px; display: block; }
.sr-form-input { border-radius: 8px; }

/* Permission Groups in Modal */
.sr-perm-group { background: #fafafa; border: 1px solid #eee; border-radius: 10px; padding: 14px 16px; }
.sr-perm-group-title { font-weight: 600; color: #41b883; font-size: 14px; margin-bottom: 10px; }
.sr-perm-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}
.sr-perm-item:last-child { border-bottom: none; }
.sr-perm-label { font-size: 14px; color: #333; font-weight: 500; }
.sr-perm-desc { font-size: 12px; color: #999; margin-top: 2px; }
</style>
<style>
#staffRoleDeleteModal .modal {
  z-index: 1060 !important;
}
</style>
